import { useCallback, useEffect, useRef, useState } from "react";
import { ANALYTICS_EVENTS } from "../analytics/analyticsContract";
import { logCanonicalAnalyticsEvent } from "../analytics/firebaseAnalytics";
import {
  getUserProfile,
  saveOnboarding,
} from "../userServices/userAccountLifecycleService";
import { classifyHomeDataError, withHomeDataTimeout } from "./homeDataLoadState";
import { readHomeSnapshot } from "./homeSnapshotCache";

export default function useOnboardingStatus(user) {
  const [onboarded, setOnboarded] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState("loading");
  const [resolvedUserUid, setResolvedUserUid] = useState(null);
  const requestSequenceRef = useRef(0);
  const userUid = user?.uid;

  const refreshOnboardingStatus = useCallback(async () => {
    const requestSequence = ++requestSequenceRef.current;
    if (!userUid) {
      setOnboarded(null);
      setOnboardingStatus("loading");
      setResolvedUserUid(null);
      return;
    }

    let cached = null;
    try {
      cached = localStorage.getItem("onboarding_" + userUid);
    } catch {
      cached = null;
    }
    if (cached) {
      setOnboarded(true);
      setOnboardingStatus("ready");
      setResolvedUserUid(userUid);
      return;
    }

    const homeSnapshot = readHomeSnapshot(userUid);
    if (homeSnapshot?.userProfile?.onboardingDone) {
      try {
        localStorage.setItem("onboarding_" + userUid, "1");
      } catch {
        // The per-account Home snapshot still lets this launch continue.
      }
      setOnboarded(true);
      setOnboardingStatus("ready");
      setResolvedUserUid(userUid);
      return;
    }

    setOnboardingStatus("loading");
    try {
      const profile = await withHomeDataTimeout(getUserProfile(userUid));
      if (requestSequence !== requestSequenceRef.current) return;
      if (profile?.onboarding || profile?.onboardingDone) {
        try {
          localStorage.setItem("onboarding_" + userUid, "1");
        } catch {
          // Storage is an optimization, not a requirement.
        }
        setOnboarded(true);
      } else {
        setOnboarded(false);
      }
      setOnboardingStatus("ready");
      setResolvedUserUid(userUid);
    } catch (error) {
      if (requestSequence !== requestSequenceRef.current) return;
      setOnboarded(null);
      setOnboardingStatus(classifyHomeDataError(error));
      setResolvedUserUid(userUid);
    }
  }, [userUid]);

  useEffect(() => {
    void refreshOnboardingStatus();
    return () => {
      requestSequenceRef.current += 1;
    };
  }, [refreshOnboardingStatus]);

  const completeOnboarding = async (answers) => {
    await saveOnboarding(userUid, answers);
    try {
      localStorage.setItem("onboarding_" + userUid, "1");
    } catch {
      // Storage is an optimization, not a requirement.
    }
    setOnboarded(true);
    setOnboardingStatus("ready");
    setResolvedUserUid(userUid);
    void logCanonicalAnalyticsEvent(ANALYTICS_EVENTS.onboardingCompleted, {
      entry_point: "web_onboarding",
      result: "saved",
    });
    return { saved: true };
  };

  const resolvedForCurrentUser = resolvedUserUid === userUid;
  return {
    completeOnboarding,
    onboarded: resolvedForCurrentUser ? onboarded : null,
    onboardingStatus: resolvedForCurrentUser ? onboardingStatus : "loading",
    retryOnboardingStatus: refreshOnboardingStatus,
  };
}
